import React from "react";

import { AlgoliaModelMapper } from "@api/mappers/products.mapper";

import {
    determineSlidesPerViewByObject,
    getBreakPointLayoutSettings,
} from "@utils/swiper";

import { useGranite } from "@redux/modules/value/translations/translations.provider";

import { Button } from "@components/General/Button/button.component";
import { ProductSlider } from "@components/Product/ProductSlider/productSlider.component";
import AutocompleteHeader from "@components/Search/SearchAutocomplete/AutocompleteItem/autocompleteHeader.container";
import { ProductsItem } from "@components/Search/SearchAutocomplete/Products/products.item.component";
import { BaseTemplateProps } from "@components/Search/SearchAutocomplete/ResultsPanel/resultsPanel.templates";
import { ProductsSourceType } from "@components/Search/SearchAutocomplete/autocompleteTypes";
import { SearchHitProvider } from "@components/Search/SearchResults/StateComponents/ResultList/searchHit.provider";

import { PRODUCT_COUNT } from "../autoCompleteConstants";
import "./products.scss";

type ProductsTemplateProps = BaseTemplateProps<ProductsSourceType>;

const ProductsTemplate = ({
    items,
    autocompleteState,
    autocompleteActions,
}: ProductsTemplateProps) => {
    const { t } = useGranite();

    const handleShowAllProductsClick = () => {
        if (!autocompleteState?.query) return;

        autocompleteActions?.submit(autocompleteState?.query);
    };

    const getShowAllProductsHeader = () => {
        const amountOfProducts = autocompleteState?.context.nbProductHits as
            | number
            | undefined;

        if (!amountOfProducts || autocompleteState?.query?.length === 0) return;

        return (
            <Button
                type={"button"}
                color={"link"}
                className={"py-2 px-0 text-primary"}
                onClick={handleShowAllProductsClick}
            >
                {t("searchBox.panel.products.header.showAll", amountOfProducts)}
            </Button>
        );
    };

    const headerTitle =
        autocompleteState?.query?.length === 0
            ? t("searchBox.panel.products.header.title.emptyQuery")
            : t("searchBox.panel.products.header.title.querying");

    return (
        <div>
            <AutocompleteHeader headerTitle={headerTitle}>
                {getShowAllProductsHeader()}
            </AutocompleteHeader>

            <div className={"productsTemplate__items--slider d-lg-none"}>
                <ProductSlider
                    title={""}
                    productsource={"handPickedSkuList"}
                    showButton={false}
                    showCategoryLink={false}
                    useInsidePadding={false}
                    amountOfFeatures={0}
                    products={items.map(AlgoliaModelMapper)}
                    breakpointslayoutsettings={determineSlidesPerViewByObject(
                        getBreakPointLayoutSettings({
                            xs: { slidesPerGroup: 1.0, slidesPerView: 3.0 },
                            sm: { slidesPerGroup: 1.0, slidesPerView: 4.0 },
                            md: { slidesPerGroup: 1.0, slidesPerView: 5.0 },
                        }),
                    )}
                    lazyLoad={false}
                />
            </div>

            <ul className={"productsTemplate__items--grid list-unstyled mb-0"}>
                {items.slice(0, PRODUCT_COUNT).map(item => (
                    <li key={item.sku}>
                        <SearchHitProvider
                            queryID={item._queryID}
                            position={item._position}
                        >
                            <ProductsItem
                                item={item}
                                autocompleteState={autocompleteState}
                                autocompleteActions={autocompleteActions}
                            />
                        </SearchHitProvider>
                    </li>
                ))}
            </ul>
        </div>
    );
};

export default ProductsTemplate;
